import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useWindowWidth } from "../../hooks/useWindowWidth";
import { useThemeContext } from "../../theme/ThemeContext";
import { RADIUS } from "../../theme/tokens";
import { Button } from "../shared";
import { overlayBackdrop } from "../../theme/styles";
import { useSwipeToClose } from "../../hooks/useSwipeToClose";

export function ViewEntryModal({ entry, onClose }) {
  const { SA, STATUS_STYLES, GLASS, isDark, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);
  const winWidth = useWindowWidth();
  const isWide = winWidth >= 768;
  const { handleProps, dragStyle } = useSwipeToClose(onClose, { enabled: !isWide });
  const s = STATUS_STYLES[entry.status] || { bg: SA.darkGray, border: SA.borderGray, text: SA.silver, dot: SA.borderGray };

  const sectionBg     = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)";
  const sectionBorder = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.10)";

  const field = (label, val) => val ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <div style={{ fontSize: 10, color: SA.silver, letterSpacing: "0.14em", textTransform: "uppercase",
        fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>{label}</div>
      <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.white, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
        letterSpacing: "0.06em" }}>{val}</div>
    </div>
  ) : null;

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 300,
      display: "flex", alignItems: isWide ? "center" : "flex-end",
      justifyContent: isWide ? "center" : "stretch",
      padding: isWide ? 24 : 0 }}>
      <div onClick={e => e.stopPropagation()} style={{
        ...GLASS.modal,
        width: "100%", maxWidth: isWide ? 480 : "100%",
        borderRadius: isWide ? 16 : "16px 16px 0 0",
        maxHeight: "88vh", display: "flex", flexDirection: "column", boxSizing: "border-box",
        animation: isWide ? "modalSlideUp 0.25s cubic-bezier(0.16,1,0.3,1)" : "sheetSlideUp 0.3s cubic-bezier(0.16,1,0.3,1)",
        ...dragStyle
      }}>
        {!isWide && (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 4px",
                        flexShrink: 0, touchAction: "none", cursor: "grab" }} {...handleProps}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: SA.borderGray }} />
          </div>
        )}
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${sectionBorder}`,
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: TYPOGRAPHY.label, color: SA.white, letterSpacing: "0.2em",
            textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 600 }}>
            Connection Details
          </span>
          <Button variant="icon" onClick={onClose}
            style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16 }}>×</Button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px 28px" }}>
          {/* Status */}
          {entry.status && (
            <div style={{ marginBottom: 16 }}>
              <span style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}`,
                padding: "4px 10px", fontSize: TYPOGRAPHY.tiny, fontWeight: 700, letterSpacing: "0.12em",
                textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif",
                display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 1 }}>
                <span style={{ width: 5, height: 5, borderRadius: RADIUS.circle, background: s.dot, display: "inline-block" }} />
                {entry.status}
              </span>
            </div>
          )}

          {/* Flight info */}
          <div style={{ background: sectionBg, border: `1px solid ${sectionBorder}`,
            borderRadius: RADIUS.md, padding: "14px 16px", marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: SA.silver, letterSpacing: "0.14em",
              textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", marginBottom: 10, fontWeight: 600 }}>
              Inbound
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: entry.eta || entry.ata ? 12 : 0 }}>
              {field("Flight", entry.inFlt)}
              {field("Origin", entry.origin)}
              {field("STA", entry.sta)}
            </div>
            {(entry.eta || entry.ata) && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                {field("ETA", entry.eta)}
                {field("ATA", entry.ata)}
              </div>
            )}
          </div>

          <div style={{ background: sectionBg, border: `1px solid ${sectionBorder}`,
            borderRadius: RADIUS.md, padding: "14px 16px", marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: SA.silver, letterSpacing: "0.14em",
              textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", marginBottom: 10, fontWeight: 600 }}>
              Outbound
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: entry.etd || entry.atd ? 12 : 0 }}>
              {field("Flight", entry.outFlt)}
              {field("Dest", entry.dest)}
              {field("STD", entry.std)}
            </div>
            {(entry.etd || entry.atd) && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                {field("ETD", entry.etd)}
                {field("ATD", entry.atd)}
              </div>
            )}
          </div>

          {/* PAX / Bags */}
          {(entry.ttlPax || entry.ttlBags) && (
            <div style={{ background: sectionBg, border: `1px solid ${sectionBorder}`,
              borderRadius: RADIUS.md, padding: "14px 16px", marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: SA.silver, letterSpacing: "0.14em",
                textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", marginBottom: 10, fontWeight: 600 }}>
                Passengers &amp; Bags
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
                {field("TTL PAX", entry.ttlPax)}
                {field("PAX EXP", entry.paxExp)}
                {field("TTL BAGS", entry.ttlBags)}
                {field("BAGS EXP", entry.bagsExp)}
              </div>
            </div>
          )}

          {/* Remark */}
          {entry.remark && (
            <div style={{ background: sectionBg, border: `1px solid ${sectionBorder}`,
              borderRadius: RADIUS.md, padding: "14px 16px" }}>
              <div style={{ fontSize: 10, color: SA.silver, letterSpacing: "0.14em",
                textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", marginBottom: 8, fontWeight: 600 }}>
                Remarks
              </div>
              <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.lightSilver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                letterSpacing: "0.04em", textTransform: "uppercase", lineHeight: 1.6 }}>
                {entry.remark}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
